import { ArchiveError } from '../app/errors';
import { config } from '../config';
import type { LogEntryRow } from '../types';
import {
  ARCHIVE_ENTITIES,
  ENTITY_SPECS,
  LOG_ENTRY_FIELDS,
  blobExtension,
} from './fields';
import type { ArchiveAudioBlobMeta, ArchiveEntity } from './fields';
import {
  canonicalArray,
  canonicalDocument,
  canonicalOrderedObject,
  canonicalRow,
  canonicalScalar,
  emptyIssues,
} from './canonical';
import type { CanonicalIssues } from './canonical';
import { SCOPE_ENTITIES, selectScope } from './scope';
import type { ArchiveRows, ArchiveScope, ArchiveScopeKind } from './scope';
import { writeZip } from './zip';
import type { ZipEntry } from './zip';

/**
 * **`serializeArchive` — the single function INV-12 names.**
 *
 * > INV-12: "An archive pushed to the backup service is byte-identical to the archive the same
 * > client would produce locally for the same scope. There is no server-only serialization path."
 *
 * Design §4 discharges that with "One `serializeArchive(rows, blobs, scope)`. BAK-1 passes
 * `scope=all`; the sync worker passes `scope={match,id}`; the PUT body **is** its output." So this
 * is not a function that produces bytes the pushed archive resembles — it is the only producer of
 * archive bytes in the app, and the drainer PUTs what it returns without touching it.
 *
 * ```
 * manifest.json          { formatVersion, scope, generatedFor, entityCounts }  ← no timestamp
 * data.json              canonical JSON, explicit field order, rows sorted by id (ULID)
 * blobs/<audioBlobId>.<ext>
 * debug/log.ndjson       BAK-1 / §13.5 exports only — see below
 * ```
 *
 * ── Two things are deliberately NOT in a pushed archive ──────────────────────
 *
 * **`Match.lastSyncedTs` / `lastSyncedHash`** (`fields.ts`) and **`debug/log.ndjson`** are excluded
 * from the bytes for the same reason, and it is stronger than §17.4's restore-comparison argument.
 * §11.3 decides whether to PUT by comparing this output's hash against the server manifest. Anything
 * inside the archive that changes as a consequence of pushing it, or as a consequence of *time
 * passing*, makes that comparison never match: every drain re-uploads, every upload changes the next
 * build's bytes, and §11.4's lost-response self-heal — which relies on a rebuild being byte-identical
 * to what the server already stored — can never fire. SYN-10 bounds pushes at three per match; one
 * such field would make it unbounded.
 *
 * The log is therefore emitted **only for `scope: 'all'`**, which is BAK-1's manual export and
 * §13.5's recovery export — neither of which is hashed or pushed. The type makes it structural:
 * `logEntries` exists only on the `all` variant of `ArchiveRequest`, so passing it with a match
 * scope does not compile.
 *
 * ── Nothing here throws on bad data ──────────────────────────────────────────
 *
 * §13.5's recovery screen exports a real BAK-1 archive **from a profile whose schema this bundle is
 * too old to understand**. An export that refuses to run on damaged data is the one failure that
 * loses it. Malformed rows, missing blobs, dangling references and unrepresentable numbers are
 * therefore recorded in `ArchiveResult.report` for the caller to log (OBS-1) and surface (NFR-8) —
 * the two conditions that *do* throw are a scope naming a match that is absent (§scope.ts) and a
 * container the zip format cannot represent (§zip.ts), because both would produce a plausible file
 * that is silently wrong.
 */

/** Bytes for one audio row. `null` when the row exists but its blob does not (§8.5, OBS-2). */
export type ArchiveBlobReader = (id: string) => Promise<Blob | null | undefined>;

interface RequestBase {
  readonly rows: ArchiveRows;
}

/**
 * Note the asymmetry, which is the point: only the `all` variant admits `logEntries`, and the
 * `profile` variant admits no blob reader because a profile archive has no audio.
 *
 * The `?: never` members are what make that a **compile error** rather than a convention.
 * TypeScript's excess-property check is per-union, not per-member: a property declared on *any*
 * member is accepted on a literal of the union type, so omitting `logEntries` from the match variant
 * would have permitted exactly the call this asymmetry exists to forbid. Declaring it as `never`
 * closes it — the property is nameable and no value fits.
 */
export type ArchiveRequest =
  | (RequestBase & {
      readonly scope: { readonly kind: 'match'; readonly matchId: string };
      readonly readBlob: ArchiveBlobReader;
      readonly logEntries?: never;
    })
  | (RequestBase & {
      readonly scope: { readonly kind: 'profile' };
      readonly readBlob?: never;
      readonly logEntries?: never;
    })
  | (RequestBase & {
      readonly scope: { readonly kind: 'all' };
      readonly readBlob: ArchiveBlobReader;
      readonly logEntries?: readonly LogEntryRow[];
    });

export interface ArchiveReport {
  /** §11.8 — keys `fields.ts` refused to publish. `backupCredential` is always among them. */
  readonly excludedSettingKeys: readonly string[];
  /** Metadata rows whose bytes the reader could not produce; the metadata is still archived. */
  readonly missingBlobBytes: readonly string[];
  /** `audioBlobId`s a row points at with no metadata row behind them (OBS-2's orphan report). */
  readonly unknownBlobIds: readonly string[];
  /** The match's `teamId` resolved to nothing. `null` in a healthy profile. */
  readonly danglingTeamId: string | null;
  /** Geometry that arrived with more precision than `GEOMETRY_DECIMALS` and was rounded here. */
  readonly roundedFieldPaths: readonly string[];
  /** Values that could not be represented and were written as `null`, each with its reason. */
  readonly coercedFieldPaths: readonly string[];
  /** Over `MAX_ARCHIVE_MB` — the service answers 413 (§15 DD-4). Reported; not enforced here. */
  readonly exceedsServiceLimit: boolean;
}

export interface ArchiveResult {
  readonly bytes: Uint8Array;
  /** Exactly what `manifest.json` carries, in `ARCHIVE_ENTITIES` order. */
  readonly entityCounts: Readonly<Partial<Record<ArchiveEntity, number>>>;
  /** Files actually written under `blobs/`. Below `entityCounts.audioBlobs` when bytes were missing. */
  readonly blobCount: number;
  readonly report: ArchiveReport;
}

// ── Entry names. Part of the format, so they are declared, not composed inline. ──

export const MANIFEST_ENTRY = 'manifest.json';
export const DATA_ENTRY = 'data.json';
export const BLOB_PREFIX = 'blobs/';
export const LOG_ENTRY_PATH = 'debug/log.ndjson';

/**
 * `manifest.generatedFor` for the two scopes that are not about one match.
 *
 * Named rather than inlined so the value an importer keys on is greppable, and so `'all'` cannot
 * collide with a match id by accident — a ULID is 26 characters and neither of these is.
 */
export const SCOPE_ID: Readonly<Record<Exclude<ArchiveScopeKind, 'match'>, string>> = {
  profile: 'profile',
  all: 'all',
};

const textEncoder = new TextEncoder();

function scopeId(scope: ArchiveScope): string {
  return scope.kind === 'match' ? scope.matchId : SCOPE_ID[scope.kind];
}

function keyOf(row: unknown, sortBy: string): string {
  if (row === null || typeof row !== 'object') return '';
  const value = (row as Record<string, unknown>)[sortBy];
  return typeof value === 'string' ? value : String(value ?? '');
}

function compare(left: string, right: string): number {
  if (left < right) return -1;
  if (left > right) return 1;
  return 0;
}

/**
 * A diagnostic path that names the **row**, not its position in whatever array it arrived in.
 *
 * `teams[TEAM0…001].notes` survives a reshuffle of the input; `teams[3].notes` does not, and a
 * report whose contents depend on read order is a report two builds of the same data disagree about.
 * A row with no primary key falls back to `#index`, which is all there is to say about it.
 */
function rowPath(entity: ArchiveEntity, row: unknown, index: number, sortBy: string): string {
  const key = keyOf(row, sortBy);
  return key === '' ? `${entity}[#${index}]` : `${entity}[${key}]`;
}

/**
 * One entity's rows: serialized first, then ordered.
 *
 * §11.7: "Rows sorted by id (ULID) — insertion order must not affect bytes." ULIDs are
 * lexicographically sortable by construction, so a plain string comparison is also chronological;
 * `settings` sorts by `key`, because it has no `id`.
 *
 * The tie-break on the serialized body is not decoration. Sorting by primary key alone is a
 * **partial** order, and `Array.prototype.sort` is stable — so two rows sharing a key, or two rows
 * that both lost theirs, would silently keep their input order and insertion order would reach the
 * bytes precisely where the data is least trustworthy. Serializing before sorting makes the total
 * order available for free.
 */
function serializeEntity(
  entity: ArchiveEntity,
  rows: readonly unknown[],
  issues: CanonicalIssues,
): string {
  const spec = ENTITY_SPECS[entity];
  const written = rows.map((row, index) => ({
    key: keyOf(row, spec.sortBy),
    body: canonicalRow(row, spec.fields, rowPath(entity, row, index, spec.sortBy), 2, issues),
  }));
  written.sort((a, b) => compare(a.key, b.key) || compare(a.body, b.body));
  return canonicalArray(
    written.map((entry) => entry.body),
    1,
  );
}

function buildManifest(
  scope: ArchiveScope,
  counts: readonly (readonly [ArchiveEntity, number])[],
  issues: CanonicalIssues,
): string {
  // Key order is declared here and nowhere else. **There is no timestamp** — §11.7 calls that "the
  // highest-value negative control — one timestamp defeats every other measure", and it is the
  // reason `formatVersion` rather than a build date is what an importer negotiates on (SYN-9).
  return canonicalDocument([
    ['formatVersion', canonicalScalar(config.ARCHIVE_FORMAT_VERSION, 1, issues)],
    ['scope', canonicalScalar(scope.kind, 1, issues)],
    ['generatedFor', canonicalScalar(scopeId(scope), 1, issues)],
    ['entityCounts', canonicalOrderedObject(counts, 'entityCounts', 1, issues)],
  ]);
}

/** One canonical JSON object per line, terminated — NDJSON, so a tail is still parseable. */
function buildLogNdjson(entries: readonly LogEntryRow[], issues: CanonicalIssues): string {
  return entries
    .map((entry, index) => `${flattenLine(canonicalRow(entry, LOG_ENTRY_FIELDS, `log[${index}]`, 0, issues))}\n`)
    .join('');
}

/** NDJSON needs one line per record, so the canonical writer's indentation is folded out. */
function flattenLine(json: string): string {
  return json.replace(/\n\s*/g, ' ');
}

async function readBlobBytes(
  meta: ArchiveAudioBlobMeta,
  read: ArchiveBlobReader,
): Promise<Uint8Array | null> {
  const blob = await read(meta.id);
  if (!blob) return null;
  return new Uint8Array(await blob.arrayBuffer());
}

/**
 * Builds one archive.
 *
 * @throws {ArchiveError} only for a `match` scope naming an absent match, or a container the zip
 *   format cannot represent. Row-level damage is reported, never thrown (see the module note).
 */
export async function serializeArchive(request: ArchiveRequest): Promise<ArchiveResult> {
  const scoped = selectScope(request.rows, request.scope);
  const issues = emptyIssues();

  const entities = SCOPE_ENTITIES[request.scope.kind];
  // `ARCHIVE_ENTITIES` is the authority on emission order; `SCOPE_ENTITIES` lists a subset of it in
  // the same order, and this assertion is what keeps that from being a coincidence somebody breaks.
  // Checked before anything is built, so a reordered scope list cannot half-produce an archive.
  assertEntityOrder(entities);

  const counts: (readonly [ArchiveEntity, number])[] = entities.map((entity) => [
    entity,
    (scoped.entities[entity] ?? []).length,
  ]);

  const members = entities.map(
    (entity) =>
      [entity, serializeEntity(entity, scoped.entities[entity] ?? [], issues)] as const,
  );

  const files: ZipEntry[] = [
    { name: MANIFEST_ENTRY, data: textEncoder.encode(buildManifest(request.scope, counts, issues)) },
    { name: DATA_ENTRY, data: textEncoder.encode(canonicalDocument(members)) },
  ];

  const metaById = new Map(
    ((scoped.entities.audioBlobs ?? []) as readonly ArchiveAudioBlobMeta[]).map((meta) => [
      meta.id,
      meta,
    ]),
  );
  const missingBlobBytes: string[] = [];

  const readBlob = request.readBlob;
  if (readBlob) {
    // One blob at a time, in id order, each `await` a yield point — §11.2's "reads blobs one at a
    // time in `['audioBlobs']`-scoped transactions with yields between them". Read order does not
    // reach the bytes (the zip sorts its entries), but it does bound peak memory to one blob.
    for (const id of scoped.blobIds) {
      const meta = metaById.get(id);
      if (!meta) continue;
      const data = await readBlobBytes(meta, readBlob);
      if (!data) {
        missingBlobBytes.push(id);
        continue;
      }
      files.push({ name: `${BLOB_PREFIX}${id}.${blobExtension(meta.mime)}`, data });
    }
  }

  // The scope check is the runtime half of the `logEntries?: never` declaration above: a caller that
  // widened its way past the type still cannot put the OBS-1 ring inside an archive that gets hashed
  // and pushed, because that would defeat §11.3's freshness comparison on every drain.
  const logEntries = request.scope.kind === 'all' ? request.logEntries : undefined;
  if (logEntries && logEntries.length > 0) {
    files.push({
      name: LOG_ENTRY_PATH,
      data: textEncoder.encode(buildLogNdjson(logEntries, issues)),
    });
  }

  const bytes = writeZip(files);

  return {
    bytes,
    entityCounts: Object.fromEntries(counts) as Partial<Record<ArchiveEntity, number>>,
    blobCount: files.filter((file) => file.name.startsWith(BLOB_PREFIX)).length,
    report: {
      excludedSettingKeys: scoped.excludedSettingKeys,
      missingBlobBytes,
      unknownBlobIds: scoped.unknownBlobIds,
      danglingTeamId: scoped.danglingTeamId,
      roundedFieldPaths: issues.rounded,
      coercedFieldPaths: issues.coerced,
      exceedsServiceLimit: bytes.length > config.MAX_ARCHIVE_MB * 1024 * 1024,
    },
  };
}

/**
 * A scope's entity list must be a subsequence of `ARCHIVE_ENTITIES`.
 *
 * Two ordered lists that must agree are two chances to be wrong. This one costs a linear scan per
 * archive and turns a reordered `SCOPE_ENTITIES` — which would silently change every future
 * archive's bytes for that scope while every existing test still passed — into an immediate throw.
 */
function assertEntityOrder(entities: readonly ArchiveEntity[]): void {
  let cursor = 0;
  for (const entity of entities) {
    const found = ARCHIVE_ENTITIES.indexOf(entity, cursor);
    if (found === -1) {
      throw new ArchiveError(
        `Scope entity "${entity}" is out of ARCHIVE_ENTITIES order; data.json's key order is not the declared one.`,
        { context: { entity } },
      );
    }
    cursor = found + 1;
  }
}
